package gr.tech.GRAttestation.sections.admin

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.GRAttestation.components.widgets.loadingSpinner
import gr.tech.GRAttestation.infrastructure.session.AdminUserSession
import gr.tech.GRAttestation.navigation.AdminSitePage
import gr.tech.GRAttestation.styles.grstyles.Theme
import gr.tech.GRAttestation.styles.noBorder
import gr.tech.GRAttestation.util.Constants.FONT_FAMILY
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

val SideNavContainerStyle = CssStyle {
    base {
        Modifier
            .fillMaxHeight()
            .backgroundColor(Colors.WhiteSmoke)
            .borderRight(1.px, LineStyle.Solid, Colors.LightGray)
            .zIndex(1)
            .boxShadow(BoxShadow.of(
                offsetX = 1.px,
                offsetY = 0.px,
                blurRadius = 10.px,
                spreadRadius = 1.px,
                color = Color.rgba(0,0,0,0.1.toFloat())
            ))
            .transition(Transition.all(250.ms))
    }
}

val FixedContainer = CssStyle {
    base {
        Modifier
            .padding(topBottom = 16.px, leftRight = 8.px)
            .height(100.svh)
            .position(Position.Fixed)
            .transition(Transition.all(250.ms))
    }
}

val NavigationItemStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .padding(8.px)
            .margin(bottom = 8.px)
            .noBorder()
            .borderRadius(5.px)
            .color(Colors.Black)
            .cursor(Cursor.Pointer)
            .overflow(Overflow.Hidden)
            .whiteSpace(WhiteSpace.NoWrap)
            .transition(Transition.all(250.ms))
    }
    hover {
        Modifier
            .backgroundColor(Colors.LightGray)
            .boxShadow(BoxShadow.of(
                offsetX = 2.px,
                offsetY = 2.px,
                blurRadius = 10.px,
                spreadRadius = 5.px,
                color = Theme.HalfGray.rgb
            ))
    }
}

val CurrentNavigationItemStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(Theme.Primary.rgb)
            .color(Colors.White)
            .cursor(Cursor.Unset)
            .boxShadow(BoxShadow.of(
                offsetX = 2.px,
                offsetY = 2.px,
                blurRadius = 10.px,
                spreadRadius = 5.px,
                color = Theme.HalfGray.rgb
            ))
    }
    hover {
        Modifier
            .backgroundColor(Theme.Primary.rgb)
            .color(Colors.White)
    }
}

val SideNavTextStyle = CssStyle {
    base {
        Modifier
            .fontFamily(FONT_FAMILY)
            .fontSize(1.cssRem)
            .fontWeight(FontWeight.Normal)
    }
}

val SideNavIconStyle = CssStyle {
    base {
        Modifier
            .margin(leftRight = autoLength, topBottom = 0.px)
    }
}

fun matchesRoute(currPath: String, basePath: String): Boolean {
    val currSegments = currPath.trim('/').split('/').drop(1)
    val baseSegments = basePath.trim('/').split('/').drop(1)

    if (currSegments.size < baseSegments.size) return false
    if (baseSegments.isEmpty() && currSegments.isNotEmpty()) return false
    return currSegments.take(baseSegments.size) == baseSegments
}


@Composable
fun AdminSideNav(
    currentRoute: String = "",
    isOpen: Boolean = true,
    onToggleOpened: () -> Unit,
    onLogout: () -> Unit,
) {
    Column(
        modifier = SideNavContainerStyle.toModifier()
            .minWidth(if (isOpen) 200.px else 70.px)
            .maxWidth(if (isOpen) 200.px else 70.px)
    ) {

        // Scrolling Container, fixed/sticky to viewport
        Column(
            modifier = FixedContainer.toModifier()
                .minWidth(if (isOpen) 200.px else 70.px)
                .maxWidth(if (isOpen) 200.px else 70.px)
        ) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(24.px)
                    .margin(topBottom = 8.px),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = if (isOpen) Arrangement.SpaceBetween else Arrangement.Center
            ) {
                if (isOpen) {
                    H1(
                        attrs = Modifier
                            .overflow(Overflow.Hidden)
                            .whiteSpace(WhiteSpace.NoWrap)
                            .margin(0.px)
                            .fontSize(1.5.cssRem)
                            .toAttrs()
                    ) {
                        Text("Dashboard")
                    }
                }

                FaBars(
                    modifier = Modifier.color(Colors.Black).cursor(Cursor.Pointer)
                        .onClick {
                            onToggleOpened()
                        },
                    size = IconSize.LG
                )
            }

            HorizontalDivider(Modifier.fillMaxWidth().borderTop(1.px, LineStyle.Solid, Colors.Grey))

            Ul(
                attrs = Modifier.fillMaxWidth().classNames("list-unstyled").toAttrs()
            ) {

                SidebarNavLink(
                    path = AdminSitePage.Home.route,
                    currPath = currentRoute,
                    isOpen = isOpen,
                    label = "Home",
                    icon = { FaHouse() }
                )

                SidebarNavLink(
                    path = AdminSitePage.Stores.route,
                    currPath = currentRoute,
                    isOpen = isOpen,
                    label = "Stores",
                    icon = { FaStore() }
                )

                SidebarNavLink(
                    path = AdminSitePage.StoreUsers.route,
                    currPath = currentRoute,
                    isOpen = isOpen,
                    label = "Users",
                    icon = { FaUser() }
                )

                SidebarNavLink(
                    path = AdminSitePage.SKUs.route,
                    currPath = currentRoute,
                    isOpen = isOpen,
                    label = "SKUs",
                    icon = { FaLightbulb() }
                )

                SidebarNavLink(
                    path = AdminSitePage.Activity.route,
                    currPath = currentRoute,
                    isOpen = isOpen,
                    label = "Activity",
                    icon = { FaClipboard(size = IconSize.LG, style = IconStyle.FILLED) }
                )
            }

            Spacer()

            HorizontalDivider(Modifier.fillMaxWidth().borderTop(1.px, LineStyle.Solid, Colors.Grey))

            Row(
                modifier = Modifier
                    .classNames("dropup")
                    .fillMaxWidth()
                    .height(45.px)
                    .margin(topBottom = 8.px),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.Start
            ) {
                Link(
                    path = "#",
                    modifier = Modifier
                        .classNames("dropdown-toggle")
                        .width(175.px)
                        .attr("data-bs-toggle", "dropdown"),
                    variant = UncoloredLinkVariant.then(UndecoratedLinkVariant),
                ) {
                    if (AdminUserSession.info.value != null && AdminUserSession.checked.value) {
                        if (isOpen) {
                            SpanText(
                                text = AdminUserSession.info.value!!.name,
                                modifier = Modifier
                                    .textOverflow(TextOverflow.Ellipsis)
                                    .whiteSpace(WhiteSpace.NoWrap)
                                    .overflow(Overflow.Hidden)
                                    .fontWeight(FontWeight.Bold)
                            )
                        }
                    } else {
                        loadingSpinner()
                    }
                }
                Ul(
                    attrs = Modifier.id("adminProfileDropdown").classNames("dropdown-menu")
                        .position(Position.Absolute)
                        .zIndex(2)
                        .toAttrs()
                ) {
                    Li {
                        Span(attrs = Modifier.classNames("dropdown-item")
                            .toAttrs {
                                onClick { onLogout() }
                            }
                        ) {
                            Text("Sign Out")
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun SidebarNavLink(
    path: String,
    currPath: String = "",
    isOpen: Boolean,
    label: String,
    icon: @Composable () -> Unit,
) {
    val matches = matchesRoute(currPath = currPath, basePath = path)

    Li(
        attrs = NavigationItemStyle.toModifier().classNames("nav-item")
            .thenIf(
                condition = matches,
                other = CurrentNavigationItemStyle.toModifier().classNames("nav-link", "active")
            ).toAttrs()
    ) {
        Link(
            path = path,
            modifier = Modifier.classNames("nav-link")
                .textAlign(if (isOpen) TextAlign.Start else TextAlign.Center)
                .thenIf(
                    condition = matches,
                    other = Modifier.color(Colors.White)
                ),
            variant = UncoloredLinkVariant.then(UndecoratedLinkVariant)
        ) {
            icon()

            if (isOpen) {
                SpanText(
                    text = label,
                    modifier = Modifier
                        .margin(left = 8.px)
                        .whiteSpace(WhiteSpace.NoWrap)
                        .overflow(Overflow.Hidden)
                )
            }
        }
    }
}